import React from 'react';
import styled, { keyframes } from 'styled-components';

import ProgressOverlay from './ProgressOverlay';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// The frame's box, letterboxed to the screen at the frame's aspect ratio. The
// screenshot and any video overlays are laid out in it in percentages, so they
// line up at any screen size.
const Box = styled.div`
  position: relative;
  max-width: 100%;
  max-height: 100%;
`;

const Screenshot = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const FadingScreenshot = styled(Screenshot)`
  animation: ${fadeIn} 600ms ease-in-out;
`;

const Video = styled.video`
  position: absolute;
  object-fit: contain;
`;

const toPercent = (fraction: number) => `${fraction * 100}%`;

const FrameView = ({
  frame,
  previousUrl,
  frameSize,
  viewport,
  durationMs,
  playKey,
}: {
  frame: WallboardScreenshots.Frame;
  previousUrl?: string;
  frameSize: { width: number; height: number };
  viewport: { width: number; height: number };
  // How long this frame is on screen, and a key that changes each time it's
  // shown (restarts the countdown overlay).
  durationMs: number;
  playKey: string;
}) => {
  const scale = Math.min(
    viewport.width / frameSize.width,
    viewport.height / frameSize.height,
  );

  return (
    <Box
      style={{
        width: frameSize.width * scale,
        height: frameSize.height * scale,
      }}
    >
      {previousUrl && previousUrl !== frame.url && (
        <Screenshot src={previousUrl} alt="" />
      )}
      {frame.url && (
        <FadingScreenshot
          src={frame.url}
          key={frame.url}
          alt=""
          data-testid="wallboard-frame"
        />
      )}
      {(frame.videos ?? []).map((video) =>
        video.url ? (
          <Video
            key={`${video.storagePath}:${video.rect.x}:${video.rect.y}`}
            src={video.url}
            autoPlay
            muted
            loop
            playsInline
            data-testid="wallboard-video"
            style={{
              left: toPercent(video.rect.x),
              top: toPercent(video.rect.y),
              width: toPercent(video.rect.width),
              height: toPercent(video.rect.height),
            }}
          />
        ) : null,
      )}
      {frame.progress && (
        <ProgressOverlay
          key={playKey}
          progress={frame.progress}
          durationMs={durationMs}
        />
      )}
    </Box>
  );
};

export default FrameView;
